import {
  Box,
  Form,
  mdBumps,
  Section,
  SectionContent,
  SectionTitle,
  xlBumps,
  xxlBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router'

import { isRenewalProposal, RenewalProposal } from '../../../domain'
import { useErrorHandlingAsyncCallback } from '../../../hooks/errors'
import {
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useUpButton } from '../../../infrastructure/routing'
import { toDateTime } from '../../../util/date'
import { AppPath } from '../../AppPath'
import { usePrincipal } from '../../authentication'
import { ErrorMessage } from '../../formBuilder'
import { CancellationReasonInput } from '../../organisms/cancellation'
import { ActionBar } from '../../organisms/form'
import { WriskTrans } from '../../organisms/trans'
import { FullPage } from '../../templates'
import { useProduct } from '../ProductProvider'
import { useProposal } from '../proposal/ProposalProvider'

const tKey = TKey('pages.renewal-opt-out')

interface OptOutFormData {
  reason: string
  other: string
}

const Content: FunctionComponent<{ proposal: RenewalProposal }> = ({ proposal }) => {
  const { t } = useWriskTranslation()

  const form = useForm<OptOutFormData>()

  const navigate = useNavigate()
  const upButton = useUpButton({ url: '..', id: 'renewal' })

  const {
    product: {
      renewal: { reasons },
    },
  } = useProduct()

  const onDismiss = useCallback(() => navigate('..'), [navigate])

  const { apiClient } = usePrincipal()

  const onConfirm = useErrorHandlingAsyncCallback(async (data: OptOutFormData) => {
    await apiClient.setPolicyAutoRenewalEnabled(proposal.sourcePolicy.policyId, {
      enabled: false,
      reason: data.reason,
    })
    navigate(AppPath.HOME)
  })

  const subheader = (
    <WriskTrans
      t={t}
      i18nKey={tKey('subheader')}
      values={{
        startAt: t(tFormats('datetime.medium'), {
          value: toDateTime(proposal.sourcePolicy.detail.expiredAt),
        }),
      }}
    />
  )

  return (
    <FullPage
      pageId='renewal-opt-out'
      header={t(tKey('header'))}
      subheader={subheader}
      upButton={upButton}
    >
      <FormProvider {...form}>
        <Form data-testid='opt-out' onSubmit={form.handleSubmit(onConfirm.execute)}>
          <Section mb={xlBumps} width={1}>
            <SectionTitle>{t(tKey('sections', 'reason'))}</SectionTitle>
            <SectionContent>
              <Controller
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, name, value } }) => (
                  <CancellationReasonInput
                    name={name}
                    onChange={onChange}
                    reasons={reasons}
                    value={value}
                    mb={mdBumps}
                  />
                )}
                name='reason'
              />
              <ErrorMessage mt={3} name='reason' tName='reason' tKey={tKey} t={t} />
            </SectionContent>
          </Section>

          <Section mb={xxlBumps} width={1}>
            <SectionTitle>{t(tKey('sections', 'end'))}</SectionTitle>
            <SectionContent>
              <Box width={1} p={mdBumps} variant='raised'>
                {t(tFormats('datetime.medium'), { value: toDateTime(proposal.startAt) })}
              </Box>
            </SectionContent>
          </Section>

          <ActionBar
            destructive
            tKey={tKey}
            loading={onConfirm.loading}
            onDismiss={onDismiss}
          />
        </Form>
      </FormProvider>
    </FullPage>
  )
}

export const RenewalOptOutPage: FunctionComponent = () => {
  const { proposal } = useProposal()

  return isRenewalProposal(proposal) ? (
    <Content proposal={proposal} />
  ) : (
    <Navigate to={AppPath.HOME} />
  )
}
